import {
  Box,
  IconButton,
  Typography,
  Button,
  Stack,
  Grid2 as Grid,
  Chip,
  TextField,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

/* ---------------- TYPES ---------------- */

interface OscarProFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

/* ---------------- MAIN FORM ---------------- */

const OscarProForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: OscarProFormProps) => {
  const providersField = configFields?.find((f: any) => f.key === 'providers');
  const otherFields = configFields?.filter((f: any) => f.key !== 'providers');

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);

    if (!existing || existing.length === 0) {
      append({
        provider_name: '',
        accept_new_patient: false,
        services: [
          {
            appointment_type: '',
            appointment_mode: [],
            template_codes: []
          }
        ]
      });
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      {true && (
        <Box>
          <Typography fontSize={16} fontWeight={600} my={2}>
            Provider Details
          </Typography>

          {providers.map((provider: any, index: number) => (
            <ProviderItem
              key={provider.id}
              index={index}
              control={control}
              remove={remove}
              disabled={disabled}
              getValues={getValues}
              setValue={setValue}
              namePrefix={namePrefix}
              providersFields={providersField}
              totalProviders={providers.length}
            />
          ))}

          <Box display='flex' justifyContent='center'>
            <Button
              variant='contained'
              disabled={disabled}
              onClick={() =>
                append({
                  provider_name: '',
                  accept_new_patient: false,
                  services: [
                    {
                      appointment_type: '',
                      appointment_mode: [],
                      template_codes: []
                    }
                  ]
                })
              }
            >
              + Add Another Provider
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default OscarProForm;

/* ---------------- PROVIDER ITEM ---------------- */

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  getValues,
  setValue,
  namePrefix,
  totalProviders,
  providersFields
}: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;
  const providerField = providersFields?.group?.find((f: any) => f.key === 'provider_name');
  const appointmentTypeField = providersFields?.group?.find((f: any) => f.key === 'appointment_type');
  const appointmentModeField = providersFields?.group?.find((f: any) => f.key === 'appointment_mode');
  const providerOptions = providerField?.options || [];
  const appointmentTypeOptions = appointmentTypeField?.options || [];
  const appointmentModeOptions = appointmentModeField?.options || [];

  const {
    fields: appointmentTypes,
    append: addAppointmentType,
    remove: removeAppointmentType
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });

  useEffect(() => {
    if (appointmentTypes.length === 0) {
      addAppointmentType({
        appointment_type: '',
        appointment_mode: [],
        template_codes: []
      });
    }
  }, [appointmentTypes.length, addAppointmentType]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>

        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      {/* Provider + Accept Patient */}
      <Grid container spacing={5} alignItems='center'>
        <Grid size={9}>
          <Select
            label='Select Provider'
            name={`${providerPrefix}.provider_name`}
            control={control}
            disabled={disabled}
            options={providerOptions}
            fullWidth
          />
        </Grid>

        <Grid size={3}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accept New Patients'
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Appointment Types */}
      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={1}>
          <Typography fontWeight={500}>Appointment Types</Typography>

          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() =>
              addAppointmentType({
                appointment_type: '',
                appointment_mode: [],
                template_codes: []
              })
            }
          >
            Add Appointment Types
          </Button>
        </Box>

        {appointmentTypes.map((appointmentType: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;

          return (
            <Box key={appointmentType.id} mb={2}>
              <Box display='flex' gap={2} alignItems='center'>
                <Select
                  label={appointmentTypeField?.label || 'Select Type'}
                  name={`${servicePrefix}.appointment_type`}
                  control={control}
                  disabled={disabled}
                  options={appointmentTypeOptions}
                  fullWidth
                />

                <Select
                  label={appointmentModeField?.label || 'Select Mode'}
                  name={`${servicePrefix}.appointment_mode`}
                  control={control}
                  disabled={disabled}
                  options={appointmentModeOptions}
                  multiple
                  fullWidth
                />

                {appointmentTypes.length > 1 && !disabled && (
                  <IconButton onClick={() => removeAppointmentType(sIndex)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                )}
              </Box>

              {/* Template Codes */}
              <Box mt={1}>
                <TemplateCodesInput
                  name={`${servicePrefix}.template_codes`}
                  getValues={getValues}
                  setValue={setValue}
                  disabled={disabled}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ---------------- TEMPLATE CODES INPUT ---------------- */

const TemplateCodesInput = ({ name, getValues, setValue, disabled }: any) => {
  const [input, setInput] = useState('');

  const values: string[] = getValues(name) || [];

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();

      const updated = [...values, input.trim()];
      setValue(name, updated);
      setInput('');
    }
  };

  const handleDelete = (code: string) => {
    const updated = values.filter((c) => c !== code);
    setValue(name, updated);
  };

  return (
    <Box border='1px solid #ECECEC' padding={2} borderRadius='4px'>
      <Typography fontWeight={500} fontSize={14}>
        Template Codes
      </Typography>
      <Box my={1} display='flex' gap={1} flexWrap='wrap'>
        {values.map((code, index) => (
          <Chip key={index} label={code} onDelete={!disabled ? () => handleDelete(code) : undefined} />
        ))}
      </Box>
      <TextField
        fullWidth
        size='small'
        placeholder='Enter template code and press Enter'
        value={input}
        disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </Box>
  );
};
